import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePursuit } from '../../contexts/PursuitContext';
import { useRiskAssessment } from '../../contexts/RiskAssessmentContext';
import {
  OriginBadge,
  CollaborationFootnote,
} from '../../components/pursuit/CollaborationBadges';
import { SelfScoreHeatmap } from '../../components/pursuit/SelfScoreHeatmap';
import { SelfScoreRollupCard } from '../../components/pursuit/SelfScoreRollupCard';
import { FederalTemplateChecklist } from '../../components/pursuit/FederalTemplateChecklist';
import PriorProposalsPanel from '../../components/pursuit/PriorProposalsPanel';
import CoverageBySourcePanel from '../../components/pursuit/CoverageBySourcePanel';
import CrossToolCoverageMapTeaser from '../../components/pursuit/CrossToolCoverageMapTeaser';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Progress } from '../../components/ui/progress';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Skeleton } from '../../components/ui/skeleton';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAutoSave } from '../../hooks/useAutoSave';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import { toast } from 'sonner';
import ScoreSourceBadge, { PreliminaryScoreNotice } from '../../components/ui/ScoreSourceBadge';
import { getEffectiveScore, getScoreSource, SCORE_SOURCE } from '../../utils/scoreSourceUtils';
import { 
  ChevronRight,
  Target,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Shield,
  Users,
  Award,
  FileText,
  Globe,
  BarChart3,
  Sparkles,
  Download,
  RefreshCw,
  Zap,
  AlertCircle,
  Link2,
  UserPlus,
  Settings2,
  ArrowLeft,
  Building2,
  Clock,
  Loader2,
  ExternalLink,
  History,
  Info,
  Share2,
  Plus,
  Trash2,
  Map
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import DecisionHistory from '../../components/pursuit/DecisionHistory';
import InviteReviewerModal from '../../components/modals/InviteReviewerModal';
import ReasoningTraceViewer from '../../components/pursuit/ReasoningTraceViewer';
import BidPursuitScorecard from '../../components/pursuit/BidPursuitScorecard';
import PwinFeedbackLoop from '../../components/pursuit/PwinFeedbackLoop';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ================================================
// HELPER FUNCTIONS
// ================================================

// Get decision recommendation based on Heuristic Fit Score
// Note: This replaces the deprecated pWin score terminology
const getDecisionFromFitScore = (score) => {
  if (score === null || score === undefined) return { verdict: 'Incomplete', color: 'slate', icon: AlertCircle };
  if (score >= 75) return { verdict: 'Green-Light Go', color: 'emerald', icon: CheckCircle2 };
  if (score >= 60) return { verdict: 'Conditional Go', color: 'amber', icon: AlertTriangle };
  return { verdict: 'Hard No-Go', color: 'red', icon: XCircle };
};

// Heat map color helper (1=Red, 5=Green)
const getScoreColor = (score) => {
  if (!score) return 'bg-slate-200';
  switch(score) {
    case 1: return 'bg-red-500';
    case 2: return 'bg-orange-500';
    case 3: return 'bg-amber-400';
    case 4: return 'bg-lime-500';
    case 5: return 'bg-emerald-500';
    default: return 'bg-slate-200';
  }
};

const getScoreText = (score) => {
  if (!score) return 'Not Rated';
  switch(score) {
    case 1: return 'Poor';
    case 2: return 'Weak';
    case 3: return 'Moderate';
    case 4: return 'Strong';
    case 5: return 'Excellent';
    default: return 'Not Rated';
  }
};

// Complexity Rating Helper based on requirement count
const getComplexityRating = (count) => {
  if (!count || count === 0) return { label: 'Unknown', color: 'text-slate-500', bgColor: 'bg-slate-100', effort: '—' };
  if (count < 25) return { label: 'Simple', color: 'text-emerald-600', bgColor: 'bg-emerald-100', effort: '2-4 weeks' };
  if (count <= 100) return { label: 'Moderate', color: 'text-amber-600', bgColor: 'bg-amber-100', effort: '4-8 weeks' };
  return { label: 'Complex', color: 'text-red-600', bgColor: 'bg-red-100', effort: '8-16 weeks' };
};

// Default What-If Scenarios
const DEFAULT_WHATIF_SCENARIOS = [
  { id: 'wif1', label: 'Add VOSB Teaming Partner', impact: 8, active: false },
  { id: 'wif2', label: 'Include ISO 27001 Certified Sub', impact: 5, active: false },
  { id: 'wif3', label: 'Reduce Price by 10%', impact: 4, active: false },
  { id: 'wif4', label: 'Add OCONUS Deployment Expert', impact: 3, active: false }
];

// Default Risk Items for Heatmap
const DEFAULT_RISKS = [
  { id: 'r1', name: 'Formatting Deadline', likelihood: 'UNLIKELY', impact: 'LOW', severity: 'LOW' },
  { id: 'r2', name: 'CPARS Performance', likelihood: 'POSSIBLE', impact: 'MEDIUM', severity: 'MEDIUM' },
  { id: 'r3', name: 'Staffing Ramp-Up', likelihood: 'LIKELY', impact: 'HIGH', severity: 'HIGH' },
  { id: 'r4', name: 'Incumbent Advantage', likelihood: 'ALMOST_CERTAIN', impact: 'VERY_HIGH', severity: 'CRITICAL' }
];

// Default Competitors
const DEFAULT_COMPETITORS = [
  { id: 'c1', name: 'OrbitLogic Technologies, Inc.', website_url: 'https://www.orbitlogic-tech.com', role: 'INCUMBENT', threat_level: 'HIGH' },
  { id: 'c2', name: 'StellarWave Federal Systems', website_url: 'https://www.stellarwavefederal.com', role: 'CHALLENGER', threat_level: 'MEDIUM' },
  { id: 'c3', name: 'AstraCore Government Services', website_url: 'https://www.astracoregsi.com', role: 'EMERGING', threat_level: 'LOW' }
];

// Default Risk Mitigations
const DEFAULT_MITIGATIONS = [
  {
    id: 'm1',
    risk: 'Incumbent Advantage',
    severity: 'HIGH',
    description: 'Agency shows a pre-solicitation note referencing a 10-year performance history with the incumbent.',
    strategy: 'Position your firm as low-risk surge capacity; propose a phased transition plan and highlight innovations the incumbent does not advertise.',
    source: 'Agency Portal',
    linkedTo: 'Knowledge Base'
  },
  {
    id: 'm2',
    risk: 'Lacks CPARS for Agency',
    severity: 'HIGH',
    description: 'Client CPARS are "Satisfactory" (3/5) and do not include this agency.',
    strategy: 'Highlight Subcontractor\'s Exceptional CPARS (5/5) and assign them visible, high-impact workshare in the organization chart.',
    source: 'CPARS Scorecards',
    linkedTo: 'Teaming Partners'
  },
  {
    id: 'm3',
    risk: 'Competitive Signals',
    severity: 'MEDIUM',
    description: 'Competitor URL scan shows the incumbent recently won a similar modernization award.',
    strategy: 'Reposition on price differentiation. Use What-If Simulator scenarios with a price-sensitive team.',
    source: 'Competitor Analysis',
    linkedTo: 'Pricing Strategy'
  }
];

// ================================================
// SUB-COMPONENTS
// ================================================

// Loading Skeleton
function GoNoGoSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center gap-4">
          <Skeleton className="w-12 h-12 rounded-lg" />
          <div>
            <Skeleton className="w-64 h-6 mb-2" />
            <Skeleton className="w-48 h-4" />
          </div>
        </div>
      </div>
      <div className="p-8">
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-3 gap-8">
              <div className="flex flex-col items-center">
                <Skeleton className="w-48 h-48 rounded-full" />
                <Skeleton className="w-32 h-8 mt-4" />
              </div>
              <div>
                <Skeleton className="w-full h-4 mb-4" />
                <Skeleton className="w-full h-4 mb-4" />
                <Skeleton className="w-3/4 h-4" />
              </div>
              <div className="space-y-4">
                <Skeleton className="w-full h-20" />
                <Skeleton className="w-full h-20" />
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="grid grid-cols-3 gap-6">
          <Skeleton className="h-64 col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    </div>
  );
}

// pWin Ring Chart Component
// Bucket a risk into low/medium/high based on its severity field
function bucketRiskSeverity(severity) {
  const s = (severity || '').toUpperCase();
  if (s === 'LOW') return 'low';
  if (s === 'MEDIUM') return 'medium';
  if (s === 'HIGH' || s === 'CRITICAL') return 'high';
  return null;
}

function computeRiskCounts(risks = []) {
  const counts = { low: 0, medium: 0, high: 0, total: 0 };
  (risks || []).forEach(r => {
    const bucket = bucketRiskSeverity(r?.severity);
    if (bucket) {
      counts[bucket] += 1;
      counts.total += 1;
    }
  });
  return counts;
}

function PwinRingChart({ pwin, riskCounts }) {
  const circumference = 2 * Math.PI * 56;
  const counts = riskCounts || { low: 0, medium: 0, high: 0, total: 0 };
  const hasRisks = counts.total > 0;

  const lowFrac = hasRisks ? counts.low / counts.total : 0;
  const mediumFrac = hasRisks ? counts.medium / counts.total : 0;
  const highFrac = hasRisks ? counts.high / counts.total : 0;

  const lowRiskStroke = lowFrac * circumference;
  const mediumRiskStroke = mediumFrac * circumference;
  const highRiskStroke = highFrac * circumference;

  return (
    <div className="relative w-48 h-48" data-testid="pwin-ring-chart">
      <svg className="w-48 h-48 transform -rotate-90">
        {/* Base track (always visible; acts as empty-state ring when no risks tracked) */}
        <circle cx="96" cy="96" r="56" stroke="#e2e8f0" strokeWidth="16" fill="none" />
        {hasRisks && (
          <>
            <circle
              cx="96" cy="96" r="56"
              stroke="#10b981"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${lowRiskStroke} ${circumference}`}
              strokeDashoffset="0"
            />
            <circle
              cx="96" cy="96" r="56"
              stroke="#f59e0b"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${mediumRiskStroke} ${circumference}`}
              strokeDashoffset={`${-lowRiskStroke}`}
            />
            <circle
              cx="96" cy="96" r="56"
              stroke="#ef4444"
              strokeWidth="16"
              fill="none"
              strokeDasharray={`${highRiskStroke} ${circumference}`}
              strokeDashoffset={`${-(lowRiskStroke + mediumRiskStroke)}`}
            />
          </>
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-5xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {pwin !== null ? `${Math.round(pwin)}%` : '—'}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">pWin</span>
      </div>
    </div>
  );
}

// Decision Badges Component
function DecisionBadges({ currentDecision, onSelect, disabled }) {
  const decisions = [
    { id: 'NO_GO', label: 'Hard No-Go', color: 'red' },
    { id: 'PURSUING', label: 'Conditional Go', color: 'amber' },
    { id: 'GO', label: 'Green-Light Go', color: 'emerald' }
  ];
  
  return (
    <div className="flex gap-2" data-testid="decision-badges">
      {decisions.map((d) => {
        const isActive = currentDecision === d.id;
        return (
          <Badge 
            key={d.id}
            data-testid={`decision-badge-${d.id}`}
            onClick={() => !disabled && onSelect(d.id)}
            className={cn(
              "px-3 py-1.5 text-xs font-medium cursor-pointer transition-all",
              disabled && "opacity-50 cursor-not-allowed",
              isActive
                ? d.color === 'red' ? "bg-red-500 text-white border-red-500" :
                  d.color === 'amber' ? "bg-amber-500 text-white border-amber-500" :
                  "bg-emerald-500 text-white border-emerald-500"
                : d.color === 'red' ? "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100" :
                  d.color === 'amber' ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100" :
                  "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            )}
          >
            {d.label}
          </Badge>
        );
      })}
    </div>
  );
}

// Dimension Score Row with Slider
function DimensionScoreRow({ label, description, value, onChange, icon: Icon, accent = 'slate', hint = null }) {
  const scoreColor = getScoreColor(value);
  const scoreText = getScoreText(value);
  const hasValue = typeof value === 'number' && value >= 1 && value <= 5;

  // When no value is set, render the slider visually pinned to the far-left
  // (position 1) with a muted style so it's obvious the dimension is
  // un-rated. Tapping the slider then registers a real rating.
  const sliderValue = hasValue ? value : 1;

  // Per-dimension accent colors for subtle visual hierarchy
  const accentMap = {
    blue:   { icon: 'text-blue-500',    ring: 'ring-blue-100'    },
    emerald:{ icon: 'text-emerald-500', ring: 'ring-emerald-100' },
    amber:  { icon: 'text-amber-500',   ring: 'ring-amber-100'   },
    purple: { icon: 'text-purple-500',  ring: 'ring-purple-100'  },
    rose:   { icon: 'text-rose-500',    ring: 'ring-rose-100'    },
    slate:  { icon: 'text-slate-500',   ring: 'ring-slate-100'   },
  };
  const accentClasses = accentMap[accent] || accentMap.slate;

  return (
    <div className={cn(
      "p-4 rounded-lg border transition-colors",
      hasValue
        ? cn("bg-slate-50 border-slate-100 ring-1", accentClasses.ring)
        : "bg-white border-dashed border-slate-200"
    )}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={cn("w-4 h-4", accentClasses.icon)} />}
          <span className="font-medium text-slate-800">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm", scoreColor)}>
            {value || '—'}
          </div>
          <span className={cn(
            "text-sm font-medium",
            value >= 4 ? "text-emerald-600" : value >= 3 ? "text-amber-600" : value ? "text-red-600" : "text-slate-400"
          )}>
            {scoreText}
          </span>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">{description}</p>
      <Slider
        value={[sliderValue]}
        onValueChange={(v) => onChange(v[0])}
        min={1}
        max={5}
        step={1}
        className={cn("w-full", !hasValue && "opacity-50")}
        data-testid={`dim-slider-${label.toLowerCase().replace(/\s+/g, '-')}`}
      />
      <div className="flex justify-between mt-1 text-xs text-slate-400">
        <span>1 - Poor</span>
        <span>5 - Excellent</span>
      </div>
      {!hasValue && (
        <p className="text-[11px] text-slate-400 italic mt-2 text-center">
          Drag the slider to rate this dimension
        </p>
      )}
      {hint}
    </div>
  );
}

// Risk Heatmap Component (5x5)
function RiskHeatmap({ risks, onCellFocusRisk, onAddRiskAt }) {
  const likelihoods = ['RARE', 'UNLIKELY', 'POSSIBLE', 'LIKELY', 'ALMOST_CERTAIN'];
  const impacts = ['VERY_LOW', 'LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'];
  const likelihoodLabels = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
  const impactLabels = ['Very Low', 'Low', 'Medium', 'High', 'Very High'];

  const getRisksAtPosition = (likelihood, impact) => {
    return (risks || []).filter(r => r?.likelihood === likelihood && r?.impact === impact);
  };

  const getCellColor = (likelihoodIdx, impactIdx) => {
    const severity = likelihoodIdx + impactIdx;
    if (severity >= 6) return 'bg-red-100 hover:bg-red-200';
    if (severity >= 4) return 'bg-orange-50 hover:bg-orange-100';
    if (severity >= 2) return 'bg-amber-50 hover:bg-amber-100';
    return 'bg-emerald-50 hover:bg-emerald-100';
  };

  const severityBandClass = (severity) =>
    severity >= 6 ? "bg-red-200 text-red-800"
      : severity >= 4 ? "bg-orange-200 text-orange-800"
      : "bg-amber-200 text-amber-800";

  const mappedCount = (risks || []).filter(
    r => r?.likelihood && r?.impact
  ).length;
  const totalCount = (risks || []).length;

  return (
    // v3.11.17 — `px-1` gives the 2px `ring-2` outline (box-shadow rendered
    // outside the cell's border-box) room to render without being clipped
    // by `overflow-x-auto`. Without this, the blue selection ring on the
    // rightmost-column cells (Very High Impact) and the focus ring on the
    // first-column cells get cut at the scroll boundary.
    <div className="overflow-x-auto px-1" data-testid="risk-heatmap">
      <div className="min-w-[500px]">
        <div className="flex items-center justify-between mb-2 gap-3">
          <p className="text-xs text-slate-500 font-medium">LIKELIHOOD →</p>
          <span
            className="text-[11px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full"
            data-testid="heatmap-risk-count"
            title="Risks placed on the heatmap out of total risks tracked"
          >
            {mappedCount} of {totalCount} risks mapped
          </span>
          <p className="text-xs text-slate-500 font-medium">IMPACT ↓</p>
        </div>

        <div className="grid grid-cols-6 gap-1">
          <div></div>
          {impactLabels.map((imp) => (
            <div key={imp} className="text-center p-1">
              <span className="text-xs text-slate-500 font-medium">{imp}</span>
            </div>
          ))}

          {likelihoods.map((lik, likIdx) => (
            <React.Fragment key={`row-${lik}`}>
              <div className="flex items-center">
                <span className="text-xs text-slate-500 font-medium">{likelihoodLabels[likIdx]}</span>
              </div>
              {impacts.map((imp, impIdx) => {
                const cellRisks = getRisksAtPosition(lik, imp);
                const primary = cellRisks[0];
                const extraCount = cellRisks.length - 1;
                const severity = likIdx + impIdx;
                const isInteractive = primary
                  ? typeof onCellFocusRisk === 'function'
                  : typeof onAddRiskAt === 'function';

                return (
                  <button
                    type="button"
                    key={`${lik}-${imp}`}
                    disabled={!isInteractive}
                    onClick={() => {
                      if (primary) {
                        onCellFocusRisk?.(primary);
                      } else {
                        onAddRiskAt?.({ likelihood: lik, impact: imp });
                      }
                    }}
                    title={
                      primary
                        ? (cellRisks.length > 1
                            ? `${cellRisks.length} risks here: ${cellRisks.map(r => r.name).join(', ')}`
                            : primary.name)
                        : isInteractive
                          ? `Add risk at ${likelihoodLabels[likIdx]} × ${impactLabels[impIdx]}`
                          : undefined
                    }
                    className={cn(
                      "relative h-14 rounded border border-slate-200 flex items-center justify-center p-1 transition-colors",
                      getCellColor(likIdx, impIdx),
                      primary && "ring-2 ring-blue-400",
                      isInteractive && "cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500",
                      !isInteractive && "cursor-default",
                      !primary && isInteractive && "hover:ring-1 hover:ring-slate-400"
                    )}
                    data-testid={`heatmap-cell-${likIdx}-${impIdx}`}
                  >
                    {primary ? (
                      <span className={cn(
                        "text-xs font-medium text-center px-1 py-0.5 rounded leading-tight line-clamp-2",
                        severityBandClass(severity)
                      )}>
                        {primary.name}
                      </span>
                    ) : (
                      isInteractive && (
                        <Plus className="w-3.5 h-3.5 text-slate-300" aria-hidden="true" />
                      )
                    )}
                    {extraCount > 0 && (
                      <span
                        className="absolute top-0.5 right-0.5 text-[10px] font-semibold bg-slate-900 text-white rounded-full w-4 h-4 flex items-center justify-center"
                        aria-label={`${extraCount} more risks in this cell`}
                      >
                        +{extraCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Severity legend */}
        <div className="flex items-center gap-4 mt-3 flex-wrap" data-testid="heatmap-legend">
          <span className="text-[11px] text-slate-500 font-medium uppercase tracking-wide">Severity:</span>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-100 border border-slate-200" />
            <span className="text-xs text-slate-600">Low</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-50 border border-slate-200" />
            <span className="text-xs text-slate-600">Moderate</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-orange-50 border border-slate-200" />
            <span className="text-xs text-slate-600">Elevated</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-100 border border-slate-200" />
            <span className="text-xs text-slate-600">High</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// What-If Simulator Component
function WhatIfSimulator({ scenarios, onToggle, basePwin, onAddScenario, onDeleteScenario }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newScenario, setNewScenario] = useState({ label: '', impact: 5, description: '' });
  
  const hasBaseline = typeof basePwin === 'number' && !Number.isNaN(basePwin);
  const activeBonus = scenarios
    .filter(s => s.active)
    .reduce((sum, s) => sum + (Number(s.impact) || 0), 0);

  const newFitScore = hasBaseline
    ? Math.max(0, Math.min(100, basePwin + activeBonus))
    : null;
  const fitScoreChange = hasBaseline ? newFitScore - basePwin : 0;
  
  const handleAdd = async () => {
    if (!newScenario.label.trim()) return;
    const success = await onAddScenario(newScenario);
    if (success) {
      setNewScenario({ label: '', impact: 5, description: '' });
      setShowAddForm(false);
    }
  };
  
  return (
    <div data-testid="what-if-simulator">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            What-If Simulator
          </h4>
          <p className="text-xs text-slate-500">Toggle scenarios to see Fit Score impact</p>
        </div>
        <div className="flex items-center gap-2">
          {hasBaseline && fitScoreChange !== 0 && (
            <Badge className={cn(
              "border",
              fitScoreChange > 0
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-red-100 text-red-700 border-red-200"
            )}>
              <TrendingUp className={cn("w-3 h-3 mr-1", fitScoreChange < 0 && "rotate-180")} />
              {fitScoreChange > 0 ? '+' : ''}{fitScoreChange}% Fit Score
            </Badge>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
            className="h-7 text-xs"
            data-testid="add-whatif-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
      </div>
      
      {/* Add Scenario Form */}
      {showAddForm && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3 space-y-2">
          <Input
            placeholder="Scenario Name (e.g., 'Add VOSB Partner')"
            value={newScenario.label}
            onChange={(e) => setNewScenario(p => ({ ...p, label: e.target.value }))}
            className="h-8 text-sm"
            data-testid="new-scenario-label"
          />
          <div className="flex gap-2 items-center">
            <span className="text-sm text-slate-600">Impact:</span>
            <input
              type="range"
              min="-10"
              max="20"
              value={newScenario.impact}
              onChange={(e) => setNewScenario(p => ({ ...p, impact: parseInt(e.target.value) }))}
              className="flex-1"
            />
            <Badge className={cn(
              "text-xs w-16 justify-center",
              newScenario.impact > 0 
                ? "bg-emerald-100 text-emerald-700" 
                : newScenario.impact < 0 
                ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-700"
            )}>
              {newScenario.impact > 0 ? '+' : ''}{newScenario.impact}%
            </Badge>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={handleAdd} className="h-8" data-testid="save-scenario-btn">Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)} className="h-8">Cancel</Button>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {scenarios.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-4 text-center">No scenarios added yet. Click "Add" to create one.</p>
        ) : (
          scenarios.map((scenario) => (
            <div 
              key={scenario.id}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg border transition-all group",
                scenario.active 
                  ? "bg-blue-50 border-blue-200" 
                  : "bg-slate-50 border-slate-200"
              )}
            >
              <div className="flex items-center gap-3">
                <Switch
                  checked={scenario.active}
                  onCheckedChange={() => onToggle(scenario.id)}
                />
                <span className="text-sm text-slate-700">{scenario.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={cn(
                  "text-xs",
                  scenario.impact > 0
                    ? "bg-emerald-100 text-emerald-700"
                    : scenario.impact < 0
                    ? "bg-red-100 text-red-700"
                    : "bg-slate-100 text-slate-700"
                )}>
                  {scenario.impact > 0 ? '+' : ''}{scenario.impact}%
                </Badge>
                <button
                  onClick={() => onDeleteScenario(scenario.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      
      <div className={cn(
        "mt-4 p-4 rounded-lg border",
        hasBaseline
          ? "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200"
          : "bg-slate-50 border-slate-200"
      )}>
        <div className="flex items-center justify-between">
          <div>
            <span className={cn(
              "text-sm font-medium block",
              hasBaseline ? "text-blue-800" : "text-slate-600"
            )}>
              Projected Fit Score with Changes
            </span>
            {!hasBaseline && (
              <span className="text-[11px] text-slate-500">
                Run AI analysis to establish a baseline Fit Score
              </span>
            )}
          </div>
          <span className={cn(
            "text-2xl font-bold",
            hasBaseline ? "text-blue-700" : "text-slate-400"
          )} style={{ fontFamily: 'Manrope, sans-serif' }}>
            {hasBaseline ? `${newFitScore}%` : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

// Risk Mitigation Panel Component
function RiskMitigationPanel({ mitigations, highlightedMitigationId, onNavigateToSource, onAddMitigation, onDeleteMitigation, onUpdateMitigation }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMitigation, setNewMitigation] = useState({ 
    risk: '', severity: 'MEDIUM', description: '', strategy: '', source: '', linked_to: '' 
  });
  const [formError, setFormError] = useState('');

  const handleAdd = async () => {
    const riskName = newMitigation.risk.trim();
    const strategy = newMitigation.strategy.trim();
    if (!riskName) {
      setFormError('Risk name is required');
      return;
    }
    if (!strategy) {
      setFormError('Mitigation strategy is required');
      return;
    }
    setFormError('');
    const success = await onAddMitigation({
      ...newMitigation,
      risk: riskName,
      strategy,
    });
    if (success) {
      setNewMitigation({ risk: '', severity: 'MEDIUM', description: '', strategy: '', source: '', linked_to: '' });
      setShowAddForm(false);
    }
  };

  // Per-severity styling that keeps LOW visually distinct from MEDIUM.
  // (Previously LOW silently rendered as amber, making it indistinguishable from MEDIUM.)
  const severityCardClass = (sev) => {
    switch (sev) {
      case 'CRITICAL':
      case 'HIGH':
        return 'border-red-200 bg-red-50/50';
      case 'MEDIUM':
        return 'border-amber-200 bg-amber-50/50';
      case 'LOW':
      default:
        return 'border-emerald-200 bg-emerald-50/40';
    }
  };

  const severityBadgeClass = (sev) => {
    switch (sev) {
      case 'CRITICAL':
      case 'HIGH':
        return 'bg-red-500 text-white';
      case 'MEDIUM':
        return 'bg-amber-500 text-white';
      case 'LOW':
      default:
        return 'bg-emerald-500 text-white';
    }
  };
  
  return (
    <div className="space-y-4" data-testid="risk-mitigation-panel">
      <div className="flex justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAddForm(!showAddForm)}
          className="h-7 text-xs"
          data-testid="add-mitigation-btn"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Mitigation
        </Button>
      </div>
      
      {/* Add Mitigation Form */}
      {showAddForm && (
        <Card className="border-blue-200 bg-blue-50/50 p-4 space-y-2">
          <div>
            <Input
              placeholder="Risk Name * (e.g., 'Incumbent Advantage')"
              value={newMitigation.risk}
              onChange={(e) => {
                setNewMitigation(p => ({ ...p, risk: e.target.value }));
                if (formError) setFormError('');
              }}
              className={cn(
                "h-8 text-sm",
                formError && !newMitigation.risk.trim() && "border-red-400 focus-visible:ring-red-400"
              )}
              data-testid="new-mitigation-risk"
            />
          </div>
          <Textarea
            placeholder="Risk Description (optional)"
            value={newMitigation.description}
            onChange={(e) => setNewMitigation(p => ({ ...p, description: e.target.value }))}
            className="text-sm min-h-[60px]"
          />
          <Textarea
            placeholder="Mitigation Strategy *"
            value={newMitigation.strategy}
            onChange={(e) => {
              setNewMitigation(p => ({ ...p, strategy: e.target.value }));
              if (formError) setFormError('');
            }}
            className={cn(
              "text-sm min-h-[60px]",
              formError && !newMitigation.strategy.trim() && "border-red-400 focus-visible:ring-red-400"
            )}
          />
          <div className="flex gap-2">
            <select
              value={newMitigation.severity}
              onChange={(e) => setNewMitigation(p => ({ ...p, severity: e.target.value }))}
              className="flex-1 h-8 text-sm rounded-md border border-slate-200 px-2"
            >
              <option value="LOW">Low Risk</option>
              <option value="MEDIUM">Medium Risk</option>
              <option value="HIGH">High Risk</option>
              <option value="CRITICAL">Critical Risk</option>
            </select>
            <Input
              placeholder="Source"
              value={newMitigation.source}
              onChange={(e) => setNewMitigation(p => ({ ...p, source: e.target.value }))}
              className="flex-1 h-8 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] text-slate-600 uppercase tracking-wide mb-1 block">
              Link to related section (optional)
            </label>
            <select
              value={newMitigation.linked_to}
              onChange={(e) => setNewMitigation(p => ({ ...p, linked_to: e.target.value }))}
              className="w-full h-8 text-sm rounded-md border border-slate-200 px-2"
              data-testid="new-mitigation-linked-to"
            >
              <option value="">None</option>
              <option value="Gap Analysis">Gap Analysis</option>
              <option value="Teaming Partners">Teaming Partners</option>
              <option value="Pricing Strategy">Pricing Strategy</option>
              <option value="Compliance">Compliance</option>
              <option value="Past Performance">Past Performance</option>
            </select>
          </div>
          {formError && (
            <p className="text-[11px] text-red-600" data-testid="mitigation-form-error">{formError}</p>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={handleAdd} className="h-8" data-testid="save-mitigation-btn">Save</Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setShowAddForm(false); setFormError(''); }}
              className="h-8"
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}
      
      {mitigations.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-4 text-center">No mitigations added yet. Click "Add Mitigation" to create one.</p>
      ) : (
        mitigations.map((item) => {
          const isHighlighted = item.id === highlightedMitigationId;
          return (
          <Card 
            key={item.id}
            data-mitigation-id={item.id}
            className={cn(
              "border group transition-all",
              severityCardClass(item.severity),
              isHighlighted && "ring-2 ring-blue-500 ring-offset-2"
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <h5 className="font-semibold text-slate-900">{item.risk}</h5>
                <div className="flex items-center gap-2">
                  {onUpdateMitigation ? (
                    <select
                      value={item.severity || 'MEDIUM'}
                      onChange={(e) => onUpdateMitigation(item.id, { severity: e.target.value })}
                      className={cn(
                        "text-[11px] font-semibold border-0 rounded px-2 py-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500",
                        severityBadgeClass(item.severity)
                      )}
                      data-testid={`mitigation-severity-${item.id}`}
                      aria-label={`Severity for ${item.risk}`}
                    >
                      <option value="LOW">LOW RISK</option>
                      <option value="MEDIUM">MEDIUM RISK</option>
                      <option value="HIGH">HIGH RISK</option>
                      <option value="CRITICAL">CRITICAL RISK</option>
                    </select>
                  ) : (
                    <Badge className={cn("text-xs", severityBadgeClass(item.severity))}>
                      {item.severity} RISK
                    </Badge>
                  )}
                  <button
                    onClick={() => onDeleteMitigation(item.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
              
              <p className="text-sm text-slate-600 mb-3">{item.description}</p>
              
              <div className="p-3 bg-white rounded border border-slate-200 mb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                  <Sparkles className="w-3 h-3 inline mr-1" />
                  AI Mitigation Strategy
                </p>
                <p className="text-sm text-slate-700">{item.strategy}</p>
              </div>
              
              {item.source && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Source: {item.source}</span>
                  {item.linked_to && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-blue-600 hover:text-blue-700 h-6 px-2"
                      onClick={() => onNavigateToSource(item.linked_to)}
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      View in {item.linked_to}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          );
        })
      )}
    </div>
  );
}

// Competitive Intelligence Component
function CompetitiveIntelligence({ competitors, onAddCompetitor, onDeleteCompetitor, onUpdateCompetitor }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', website_url: '', role: 'CHALLENGER', threat_level: 'MEDIUM' });
  const [formError, setFormError] = useState('');

  const incumbents = (competitors || []).filter(c => c?.role === 'INCUMBENT');
  const challengerCount = (competitors || []).filter(c => c?.role === 'CHALLENGER').length;
  const emergingCount = (competitors || []).filter(c => c?.role === 'EMERGING').length;

  const roleColors = {
    INCUMBENT: 'bg-red-100 text-red-700 border-red-200',
    CHALLENGER: 'bg-amber-100 text-amber-700 border-amber-200',
    EMERGING: 'bg-slate-100 text-slate-700 border-slate-200'
  };

  const threatColors = {
    HIGH: 'text-red-600',
    MEDIUM: 'text-amber-600',
    LOW: 'text-emerald-600'
  };

  const threatBadgeColors = {
    HIGH: 'bg-red-50 text-red-700 border-red-200',
    MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
    LOW: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  };

  const handleAdd = async () => {
    const trimmedName = newCompetitor.name.trim();
    if (!trimmedName) {
      setFormError('Competitor name is required');
      return;
    }
    setFormError('');
    const success = await onAddCompetitor({ ...newCompetitor, name: trimmedName });
    if (success) {
      setNewCompetitor({ name: '', website_url: '', role: 'CHALLENGER', threat_level: 'MEDIUM' });
      setShowAddForm(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="competitive-intelligence">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Known Competitors
          </h4>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(!showAddForm)}
            className="h-7 text-xs"
            data-testid="add-competitor-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Manually tracked competitors. Enrich with SAM.gov award history and public sources as you research.
        </p>
        
        {/* Add Competitor Form */}
        {showAddForm && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3 space-y-2">
            <div>
              <Input
                placeholder="Competitor Name *"
                value={newCompetitor.name}
                onChange={(e) => {
                  setNewCompetitor(p => ({ ...p, name: e.target.value }));
                  if (formError) setFormError('');
                }}
                className={cn("h-8 text-sm", formError && "border-red-400 focus-visible:ring-red-400")}
                data-testid="new-competitor-name"
              />
              {formError && (
                <p className="text-[11px] text-red-600 mt-1" data-testid="competitor-form-error">{formError}</p>
              )}
            </div>
            <Input
              placeholder="Website URL (optional)"
              value={newCompetitor.website_url}
              onChange={(e) => setNewCompetitor(p => ({ ...p, website_url: e.target.value }))}
              className="h-8 text-sm"
            />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-slate-600 uppercase tracking-wide mb-1 block">Role</label>
                <select
                  value={newCompetitor.role}
                  onChange={(e) => setNewCompetitor(p => ({ ...p, role: e.target.value }))}
                  className="w-full h-8 text-sm rounded-md border border-slate-200 px-2"
                  data-testid="new-competitor-role"
                >
                  <option value="INCUMBENT">Incumbent</option>
                  <option value="CHALLENGER">Challenger</option>
                  <option value="EMERGING">Emerging</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] text-slate-600 uppercase tracking-wide mb-1 block">Threat</label>
                <select
                  value={newCompetitor.threat_level}
                  onChange={(e) => setNewCompetitor(p => ({ ...p, threat_level: e.target.value }))}
                  className="w-full h-8 text-sm rounded-md border border-slate-200 px-2"
                  data-testid="new-competitor-threat"
                >
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" onClick={handleAdd} className="h-8" data-testid="save-competitor-btn">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setFormError(''); }} className="h-8">Cancel</Button>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          {competitors.length === 0 ? (
            <p className="text-sm text-slate-500 italic py-4 text-center">No competitors added yet. Click "Add" to track a competitor.</p>
          ) : (
            competitors.map((comp) => (
              <div 
                key={comp.id}
                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100 group gap-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{comp.name}</p>
                  {comp.website_url && (
                    <a 
                      href={comp.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      {comp.website_url.replace('https://www.', '').replace('https://', '')}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {onUpdateCompetitor ? (
                    <>
                      <select
                        value={comp.role || 'CHALLENGER'}
                        onChange={(e) => onUpdateCompetitor(comp.id, { role: e.target.value })}
                        className={cn(
                          "text-[11px] font-medium border rounded-md px-1.5 py-0.5 cursor-pointer",
                          roleColors[comp.role] || roleColors.CHALLENGER
                        )}
                        data-testid={`competitor-role-${comp.id}`}
                      >
                        <option value="INCUMBENT">INCUMBENT</option>
                        <option value="CHALLENGER">CHALLENGER</option>
                        <option value="EMERGING">EMERGING</option>
                      </select>
                      <select
                        value={comp.threat_level || 'MEDIUM'}
                        onChange={(e) => onUpdateCompetitor(comp.id, { threat_level: e.target.value })}
                        className={cn(
                          "text-[11px] font-medium border rounded-md px-1.5 py-0.5 cursor-pointer",
                          threatBadgeColors[comp.threat_level] || threatBadgeColors.MEDIUM
                        )}
                        data-testid={`competitor-threat-${comp.id}`}
                      >
                        <option value="HIGH">HIGH</option>
                        <option value="MEDIUM">MED</option>
                        <option value="LOW">LOW</option>
                      </select>
                    </>
                  ) : (
                    <>
                      <Badge className={cn("text-xs border", roleColors[comp.role] || roleColors.CHALLENGER)}>
                        {comp.role?.replace('_', ' ')}
                      </Badge>
                      <Badge className={cn("text-xs border", threatBadgeColors[comp.threat_level] || threatBadgeColors.MEDIUM)}>
                        {comp.threat_level || 'MEDIUM'}
                      </Badge>
                    </>
                  )}
                  <button
                    onClick={() => onDeleteCompetitor(comp.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-100 rounded"
                    data-testid={`delete-competitor-${comp.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      {/* Incumbent Snapshot — derived from actual competitors marked as INCUMBENT */}
      <div className={cn(
        "p-4 rounded-lg border",
        incumbents.length > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"
      )} data-testid="incumbent-snapshot">
        <div className="flex items-center gap-2 mb-3">
          <AlertCircle className={cn("w-4 h-4", incumbents.length > 0 ? "text-red-600" : "text-slate-500")} />
          <h5 className={cn(
            "font-semibold",
            incumbents.length > 0 ? "text-red-800" : "text-slate-700"
          )}>
            Incumbent Snapshot
          </h5>
        </div>

        {incumbents.length === 0 ? (
          <div className="text-sm text-slate-600">
            <p className="mb-2">No incumbent identified.</p>
            <p className="text-xs text-slate-500">
              Mark a competitor as <span className="font-medium">Incumbent</span> above to see displacement context here.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs text-red-700 mb-3">
              {incumbents.length === 1
                ? 'One incumbent detected — displacement risk applies.'
                : `${incumbents.length} incumbents detected — displacement risk applies.`}
            </p>
            <div className="space-y-2">
              {incumbents.map((inc) => (
                <div
                  key={inc.id}
                  className="flex items-center justify-between p-2 bg-white rounded border border-red-100"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{inc.name}</p>
                    {inc.website_url && (
                      <a
                        href={inc.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-600 hover:text-blue-700 flex items-center gap-1 truncate"
                      >
                        {inc.website_url.replace('https://www.', '').replace('https://', '')}
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    )}
                  </div>
                  <span className={cn(
                    "text-xs font-semibold whitespace-nowrap ml-2",
                    threatColors[inc.threat_level] || 'text-slate-600'
                  )}>
                    {inc.threat_level || 'MEDIUM'} threat
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-red-100 flex items-center justify-between text-xs text-slate-600">
              <span>Challengers tracked</span>
              <span className="font-semibold text-slate-800">{challengerCount}</span>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-600 mt-1">
              <span>Emerging players</span>
              <span className="font-semibold text-slate-800">{emergingCount}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Requirement Coverage Component
function RequirementCoverage({ gaps }) {
  const total = gaps.length;
  const covered = gaps.filter(g => g.status === 'MET').length;
  const partial = gaps.filter(g => g.status === 'PARTIAL').length;
  const gapCount = gaps.filter(g => g.status === 'GAP').length;
  const unmapped = Math.max(0, total - covered - partial - gapCount);

  // Use exact fractional widths for segmented bar (prevents rounding drift)
  const coveredWidth = total > 0 ? (covered / total) * 100 : 0;
  const partialWidth = total > 0 ? (partial / total) * 100 : 0;
  const gapWidth = total > 0 ? (gapCount / total) * 100 : 0;
  const unmappedWidth = total > 0 ? (unmapped / total) * 100 : 0;

  // Header percent reflects "Met" coverage only
  const coveragePercent = total > 0 ? Math.round((covered / total) * 100) : 0;

  // Dynamic color for header percent
  const coverageColor =
    total === 0 ? 'text-slate-400' :
    coveragePercent >= 80 ? 'text-emerald-600' :
    coveragePercent >= 50 ? 'text-amber-600' :
    'text-red-600';

  return (
    <div data-testid="requirement-coverage">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Requirement Coverage
        </h4>
        <span className={cn("text-2xl font-bold", coverageColor)} data-testid="coverage-percent">
          {coveragePercent}%
        </span>
      </div>

      {total > 0 ? (
        <>
          <div className="h-4 rounded-full overflow-hidden flex bg-slate-200">
            <div
              className="bg-emerald-500 transition-all"
              style={{ width: `${coveredWidth}%` }}
            />
            <div
              className="bg-amber-400 transition-all"
              style={{ width: `${partialWidth}%` }}
            />
            <div
              className="bg-red-400 transition-all"
              style={{ width: `${gapWidth}%` }}
            />
            {unmapped > 0 && (
              <div
                className="bg-slate-400 transition-all"
                style={{ width: `${unmappedWidth}%` }}
              />
            )}
          </div>

          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-emerald-500 rounded"></div>
              <span className="text-xs text-slate-600">Met ({covered})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-400 rounded"></div>
              <span className="text-xs text-slate-600">Partial ({partial})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              <span className="text-xs text-slate-600">Gaps ({gapCount})</span>
            </div>
            {unmapped > 0 && (
              <div className="flex items-center gap-1" title="Requirements with no coverage status assigned">
                <div className="w-3 h-3 bg-slate-400 rounded"></div>
                <span className="text-xs text-slate-600">Unmapped ({unmapped})</span>
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-slate-500 italic">Add requirements in Gap Analysis to track coverage</p>
      )}
    </div>
  );
}

// ================================================
// MAIN COMPONENT
// ================================================
export default function GoNoGoDashboardLive() {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const { authToken, getAuthHeaders, user } = useAuth();
  const { activePursuit, enterPursuit } = usePursuit();
  const { updatePursuit } = useRiskAssessment();
  
  // Data states
  const [pursuit, setPursuit] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [extractedReqs, setExtractedReqs] = useState([]);
  const [pwinComposite, setPwinComposite] = useState(null);
  const [whatIfScenarios, setWhatIfScenarios] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [risks, setRisks] = useState([]);
  const [mitigations, setMitigations] = useState([]);

  // Derived risk distribution from the tracked risks[] array (drives the pWin ring segments + legend)
  const riskCounts = useMemo(() => computeRiskCounts(risks), [risks]);

  // v3.11.15 — Unified gap source for Strengths / Critical Gaps / Coverage.
  // The Gap Analysis collection is manually populated and is empty for many
  // pursuits where users instead drive coverage through extracted_requirements
  // (compliance_status MET / PARTIAL / GAP). When gap_analysis is empty we
  // fall back to extracted_requirements so the Executive Verdict reflects
  // real coverage data instead of showing 0/0/0.
  const unifiedGaps = useMemo(() => {
    if (gaps && gaps.length > 0) return gaps;
    return (extractedReqs || []).map((r) => ({
      id: r.id,
      requirement_name: r.requirement_text || r.requirement_number || 'Untitled requirement',
      requirement_source: r.source_document_name || r.section_reference || null,
      // Map compliance_status → gap status. NOT_EVALUATED / null become 'UNMAPPED'
      // which the coverage bar already handles via the unmapped bucket.
      status: ['MET', 'PARTIAL', 'GAP'].includes(r.compliance_status)
        ? r.compliance_status
        : 'UNMAPPED',
    }));
  }, [gaps, extractedReqs]);
  
  // Track if intel data was loaded from DB
  const [intelLoaded, setIntelLoaded] = useState(false);
  
  // New: Decision notes and compliance insight states
  const [decisionNotes, setDecisionNotes] = useState('');
  const [complianceInsight, setComplianceInsight] = useState(null);
  const [requirementCount, setRequirementCount] = useState(0);
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [auditStatus, setAuditStatus] = useState(null); // Track compliance audit status
  // Federal-template self-score telemetry — informs (does NOT auto-set)
  // the Technical Capability + Past Performance Fit sliders below.
  const [selfScoreSummary, setSelfScoreSummary] = useState(null);
  
  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  
  // P0: New states for Decision History and Reviewer Access
  const [activeTab, setActiveTab] = useState('analysis');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [decisionHistoryKey, setDecisionHistoryKey] = useState(0); // For refreshing history

  // Section 6 — Cross-linking heatmap → mitigation card.
  // When the user clicks a filled heatmap cell, we flash-highlight the matching
  // mitigation and scroll it into view. Matching is by case-insensitive name.
  const [highlightedMitigationId, setHighlightedMitigationId] = useState(null);

  // Footer — confirmation gate for the destructive "Mark as Hard No-Go" path.
  const [showNoGoConfirm, setShowNoGoConfirm] = useState(false);

  // Dirty-state tracking for the decision notes textarea. The user may type
  // rationale and forget to hit Enter / blur, then click No-Go expecting the
  // text to be part of the snapshot. We surface a clear indicator in the
  // footer so there's no ambiguity.
  const hasUnsavedRationale = (decisionNotes || '').trim() !== ((evaluation?.decision_notes) || '').trim();

  const handleFocusRiskInMitigations = useCallback((risk) => {
    if (!risk) return;
    const target = (mitigations || []).find(
      (m) => (m?.risk || '').trim().toLowerCase() === (risk.name || '').trim().toLowerCase()
    );
    if (!target) {
      toast.info(`No mitigation added yet for "${risk.name}". Use "Add Mitigation".`);
      return;
    }
    setHighlightedMitigationId(target.id);
    // Scroll into view after the next paint so the ref is bound.
    setTimeout(() => {
      const el = document.querySelector(`[data-mitigation-id="${target.id}"]`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    // Clear the highlight after ~2.5s so the flash is unobtrusive.
    setTimeout(() => setHighlightedMitigationId(null), 2500);
  }, [mitigations]);


  // Auto-save hook
  const saveEvaluation = useCallback(async (data) => {
    const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/evaluation`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to save');
    return await response.json();
  }, [pursuitId, getAuthHeaders]);

  const { save, isSaving, lastSaved, error: saveError } = useAutoSave(saveEvaluation, 1000);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!pursuitId || !authToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const headers = getAuthHeaders();
      
      // Fetch pursuit details
      const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers });
      if (!pursuitRes.ok) {
        if (pursuitRes.status === 404) {
          setError('Pursuit not found');
          return;
        }
        throw new Error('Failed to fetch pursuit');
      }
      const pursuitData = await pursuitRes.json();
      setPursuit(pursuitData);
      
      // Enter pursuit context
      enterPursuit({
        id: pursuitData.id,
        title: pursuitData.opportunity_name,
        rfpNumber: pursuitData.solicitation_number || pursuitData.agency_code,
        fitScore: getEffectiveScore(pursuitData).score,
        dueDate: pursuitData.response_deadline
      });
      
      // Fetch evaluation
      const evalRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/evaluation`, { headers });
      let evalData = null;
      if (evalRes.ok) {
        evalData = await evalRes.json();
        setEvaluation(evalData);
        // Load existing decision notes
        if (evalData.decision_notes) {
          setDecisionNotes(evalData.decision_notes);
        }
      }
      
      // v3.11.19 — Update the cross-component pursuit cache, but do NOT
      // call `showHUD()` here. The Go/No-Go Dashboard renders its own
      // Executive Verdict ring + pWin Signal Tracker, so the floating
      // HUD becomes redundant chrome on this screen. Visibility is now
      // owned exclusively by `DashboardLayout` which only enables the
      // HUD on Opportunity Intake + Document Ingestion routes.
      updatePursuit({ ...pursuitData, evaluation: evalData });
      
      // Fetch gaps for coverage section
      const gapsRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, { headers });
      if (gapsRes.ok) {
        setGaps(await gapsRes.json());
      }

      // v3.11.15 — Fetch extracted requirements as the secondary coverage source.
      // Used when gap_analysis is empty (most common case) so Strengths /
      // Critical Gaps / Requirement Coverage reflect compliance_status data
      // captured during Shred / AI extraction instead of showing 0.
      try {
        const reqRes = await fetch(`${API_URL}/api/ai/requirements/${pursuitId}`, { headers });
        if (reqRes.ok) {
          setExtractedReqs(await reqRes.json());
        }
      } catch (e) {
        // Non-blocking — fall back to gap_analysis only.
      }

      // v3.11.15 — Fetch the live composite pWin so the Executive Verdict ring
      // matches the pWin Signal Tracker. Previously the Verdict used the raw
      // AI Analysis score (competitive_alignment_score) while the Tracker
      // showed the multi-signal composite, so users saw e.g. 30% vs 35%.
      try {
        const compRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/pwin-composite`, { headers });
        if (compRes.ok) {
          setPwinComposite(await compRes.json());
        }
      } catch (e) {
        // Non-blocking — fall back to legacy pwin_score on pursuit.
      }
      
      // Fetch requirement count for complexity indicator
      const reqCountRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/requirement-count`, { headers });
      if (reqCountRes.ok) {
        const reqData = await reqCountRes.json();
        setRequirementCount(reqData.total_requirements);
      }

      // Fetch self-score summary — federal 0-3 rubric distribution for the
      // Technical Capability + PP Fit slider hints.
      try {
        const ssRes = await fetch(`${API_URL}/api/ai/requirements/${pursuitId}/self-score-summary`, { headers });
        if (ssRes.ok) {
          setSelfScoreSummary(await ssRes.json());
        }
      } catch (e) {
        // Non-blocking — slider hints just won't render
      }
      
      // Fetch Go/No-Go Intelligence Data (competitors, risks, mitigations, what-if scenarios)
      const intelRes = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/all`, { headers });
      if (intelRes.ok) {
        const intelData = await intelRes.json();
        
        // Only use DB data if it exists, otherwise keep defaults empty to show "Add" prompts
        if (intelData.competitors && intelData.competitors.length > 0) {
          setCompetitors(intelData.competitors);
        }
        if (intelData.risks && intelData.risks.length > 0) {
          setRisks(intelData.risks);
        }
        if (intelData.mitigations && intelData.mitigations.length > 0) {
          setMitigations(intelData.mitigations);
        }
        if (intelData.whatif_scenarios && intelData.whatif_scenarios.length > 0) {
          setWhatIfScenarios(intelData.whatif_scenarios);
        }
        setIntelLoaded(true);
      }
      
    } catch (err) {
      console.error('Error fetching Go/No-Go data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [pursuitId, authToken, getAuthHeaders, enterPursuit, updatePursuit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // v3.11.15 — When any upstream signal changes (Scorecard, Cap Matrix, RTM,
  // Self-Score), the PwinFeedbackLoop / other tools dispatch
  // `pwin-signal-updated`. Refresh the composite + pursuit state so the
  // Executive Verdict ring stays in sync with the Signal Tracker.
  useEffect(() => {
    if (!pursuitId || !authToken) return;
    const handler = async (evt) => {
      if (evt?.detail?.pursuitId && evt.detail.pursuitId !== pursuitId) return;
      try {
        const headers = getAuthHeaders();
        const compRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/pwin-composite`, { headers });
        if (compRes.ok) {
          setPwinComposite(await compRes.json());
        }
      } catch (e) {
        // Silent — composite stays at last good value
      }
    };
    window.addEventListener('pwin-signal-updated', handler);
    return () => window.removeEventListener('pwin-signal-updated', handler);
  }, [pursuitId, authToken, getAuthHeaders]);
  
  // Polling for compliance audit completion (auto-refresh every 5 seconds)
  useEffect(() => {
    if (!pursuitId || !authToken) return;
    
    // Track the last known pwin score for change detection
    let lastKnownPwin = pursuit?.pwin_score;
    let lastKnownRiskLevel = pursuit?.risk_level;
    
    const pollInterval = setInterval(async () => {
      try {
        const headers = getAuthHeaders();
        const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers });
        if (pursuitRes.ok) {
          const pursuitData = await pursuitRes.json();
          
          // Check if pwin_score OR risk_level changed (indicates compliance audit completed)
          const pwinChanged = pursuitData.pwin_score !== lastKnownPwin;
          const riskChanged = pursuitData.risk_level !== lastKnownRiskLevel;
          
          if (pwinChanged || riskChanged) {
            setPursuit(pursuitData);
            lastKnownPwin = pursuitData.pwin_score;
            lastKnownRiskLevel = pursuitData.risk_level;
            setLastRefreshed(new Date().toLocaleTimeString());
            setAuditStatus('COMPLETE');
            
            // Show toast notification
            if (pwinChanged) {
              const scoreDiff = pursuitData.pwin_score - (pursuit?.pwin_score || 0);
              const direction = scoreDiff >= 0 ? '↑' : '↓';
              toast.success(`Heuristic Fit Score Updated: ${pursuit?.pwin_score || 0}% → ${pursuitData.pwin_score}% ${direction}`, {
                description: 'Compliance Audit Complete'
              });
            }
            
            // Also refresh evaluation data
            const evalRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/evaluation`, { headers });
            if (evalRes.ok) {
              const evalData = await evalRes.json();
              setEvaluation(evalData);
            }
          }
        }
      } catch (err) {
        // Silent fail for polling
      }
    }, 5000); // Poll every 5 seconds (was 10 seconds)
    
    return () => clearInterval(pollInterval);
  }, [pursuitId, authToken, getAuthHeaders, pursuit]);

  // Handle score change
  const handleScoreChange = (field, value) => {
    // When user manually adjusts a dimension, clear the auto_seeded flag
    const updates = { [field]: value };
    if (evaluation?.auto_seeded) {
      updates.auto_seeded = false;
    }
    setEvaluation(prev => ({ ...prev, ...updates }));
    save(updates);
  };

  // Whether any of the 5 dimension sliders has a value (used to show reset button)
  const hasAnyDimensionScore = Boolean(
    evaluation?.technical_capability ||
    evaluation?.past_performance_fit ||
    evaluation?.financial_risk ||
    evaluation?.management_approach ||
    evaluation?.competitive_positioning
  );

  const [isResettingToAi, setIsResettingToAi] = useState(false);

  const handleResetToAiDefaults = async () => {
    if (!pursuitId) return;
    const confirmed = window.confirm(
      'Reset all 5 dimension scores back to the AI-derived defaults? Your current slider adjustments will be overwritten.'
    );
    if (!confirmed) return;
    setIsResettingToAi(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/evaluation/${pursuitId}/reset-to-ai-defaults`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.evaluation) {
        setEvaluation(data.evaluation);
      }
      toast.success('Dimensions reset to AI defaults');
    } catch (err) {
      console.error('Reset to AI defaults failed:', err);
      toast.error('Failed to reset to AI defaults. Please try again.');
    } finally {
      setIsResettingToAi(false);
    }
  };

  // Handle decision change with snapshot creation
  const handleDecisionChange = async (decision) => {
    // For NO_GO, require notes
    if (decision === 'NO_GO' && !decisionNotes.trim()) {
      toast.error('Decision notes are required for No-Go decisions');
      return;
    }
    
    setIsCreatingSnapshot(true);
    
    try {
      // Create decision snapshot (silent background process)
      const headers = getAuthHeaders();
      const snapshotRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/decision-snapshot`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          decision: decision,
          decision_notes: decisionNotes || null,
          compliance_snapshot: complianceInsight
        })
      });

      if (!snapshotRes.ok) {
        // Bubble up the failure so the UI does not silently claim success.
        let detail = `Failed to record ${decision.replace('_', '-')} decision`;
        try {
          const body = await snapshotRes.json();
          if (typeof body?.detail === 'string' && body.detail.length < 200) {
            detail = body.detail;
          }
        } catch {
          // ignore
        }
        throw new Error(detail);
      }

      const snapshot = await snapshotRes.json();
      console.log('✅ Decision Snapshot Created:', snapshot);
      toast.success(`${decision.replace('_', '-')} decision recorded`);
      setDecisionHistoryKey(prev => prev + 1);

      // Update local state only after a confirmed snapshot write.
      setEvaluation(prev => ({ ...prev, decision, decision_notes: decisionNotes }));
    } catch (err) {
      console.error('Error creating snapshot:', err);
      toast.error(err?.message || 'Failed to record decision. Please try again.');
      // Do NOT mutate evaluation — the decision did not actually persist.
    } finally {
      setIsCreatingSnapshot(false);
    }
  };

  // Handle What-If toggle
  const handleWhatIfToggle = async (id) => {
    const scenario = whatIfScenarios.find(s => s.id === id);
    if (!scenario) return;
    
    const newActive = !scenario.active;
    
    // Optimistic update
    setWhatIfScenarios(prev => prev.map(s => 
      s.id === id ? { ...s, active: newActive } : s
    ));
    
    // Save to DB
    try {
      const headers = getAuthHeaders();
      await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/whatif-scenarios/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ active: newActive })
      });
    } catch (err) {
      console.error('Failed to update what-if scenario:', err);
      // Revert on error
      setWhatIfScenarios(prev => prev.map(s => 
        s.id === id ? { ...s, active: !newActive } : s
      ));
    }
  };
  
  // ============================================================================
  // CRUD Handlers for Go/No-Go Intelligence Data
  // ============================================================================
  
  // Add Competitor
  const handleAddCompetitor = async (competitorData) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/competitors`, {
        method: 'POST',
        headers,
        body: JSON.stringify(competitorData)
      });
      if (res.ok) {
        const { competitor } = await res.json();
        setCompetitors(prev => [competitor, ...prev]);
        toast.success('Competitor added');
        return true;
      }
    } catch (err) {
      console.error('Failed to add competitor:', err);
      toast.error('Failed to add competitor');
    }
    return false;
  };
  
  // Delete Competitor
  const handleDeleteCompetitor = async (competitorId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/competitors/${competitorId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setCompetitors(prev => prev.filter(c => c.id !== competitorId));
        toast.success('Competitor removed');
      }
    } catch (err) {
      console.error('Failed to delete competitor:', err);
      toast.error('Failed to remove competitor');
    }
  };

  // Update Competitor (role / threat_level / etc.)
  const handleUpdateCompetitor = async (competitorId, patch) => {
    // Optimistic update
    const previous = competitors;
    setCompetitors(prev => prev.map(c => c.id === competitorId ? { ...c, ...patch } : c));
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/competitors/${competitorId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(patch)
      });
      if (!res.ok) throw new Error('Update failed');
    } catch (err) {
      console.error('Failed to update competitor:', err);
      toast.error('Failed to update competitor');
      setCompetitors(previous);
    }
  };
  
  // Add Risk
  const handleAddRisk = async (riskData) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/risks`, {
        method: 'POST',
        headers,
        body: JSON.stringify(riskData)
      });
      if (res.ok) {
        const { risk } = await res.json();
        setRisks(prev => [risk, ...prev]);
        toast.success('Risk added');
        return true;
      }
    } catch (err) {
      console.error('Failed to add risk:', err);
      toast.error('Failed to add risk');
    }
    return false;
  };
  
  // Delete Risk
  const handleDeleteRisk = async (riskId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/risks/${riskId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setRisks(prev => prev.filter(r => r.id !== riskId));
        toast.success('Risk removed');
      }
    } catch (err) {
      console.error('Failed to delete risk:', err);
      toast.error('Failed to remove risk');
    }
  };
  
  // Add Mitigation
  const handleAddMitigation = async (mitigationData) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/mitigations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(mitigationData)
      });
      if (res.ok) {
        const { mitigation } = await res.json();
        setMitigations(prev => [mitigation, ...prev]);
        toast.success('Mitigation added');
        return true;
      }
    } catch (err) {
      console.error('Failed to add mitigation:', err);
      toast.error('Failed to add mitigation');
    }
    return false;
  };
  
  // Delete Mitigation
  const handleDeleteMitigation = async (mitigationId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/mitigations/${mitigationId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setMitigations(prev => prev.filter(m => m.id !== mitigationId));
        toast.success('Mitigation removed');
      }
    } catch (err) {
      console.error('Failed to delete mitigation:', err);
      toast.error('Failed to remove mitigation');
    }
  };

  // Update Mitigation (severity / risk name / linked_to / etc.)
  const handleUpdateMitigation = async (mitigationId, patch) => {
    const previous = mitigations;
    // Optimistic update
    setMitigations(prev => prev.map(m => m.id === mitigationId ? { ...m, ...patch } : m));
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/mitigations/${mitigationId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(patch)
      });
      if (!res.ok) throw new Error('Update failed');
    } catch (err) {
      console.error('Failed to update mitigation:', err);
      toast.error('Failed to update mitigation');
      setMitigations(previous);
    }
  };
  
  // Add What-If Scenario
  const handleAddWhatIfScenario = async (scenarioData) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/whatif-scenarios`, {
        method: 'POST',
        headers,
        body: JSON.stringify(scenarioData)
      });
      if (res.ok) {
        const { scenario } = await res.json();
        setWhatIfScenarios(prev => [scenario, ...prev]);
        toast.success('What-If scenario added');
        return true;
      }
    } catch (err) {
      console.error('Failed to add what-if scenario:', err);
      toast.error('Failed to add scenario');
    }
    return false;
  };
  
  // Delete What-If Scenario
  const handleDeleteWhatIfScenario = async (scenarioId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/gonogo-intel/${pursuitId}/whatif-scenarios/${scenarioId}`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setWhatIfScenarios(prev => prev.filter(s => s.id !== scenarioId));
        toast.success('Scenario removed');
      }
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      toast.error('Failed to remove scenario');
    }
  };

  // Handle refresh score - triggers Compliance Audit
  const handleRefreshScore = async () => {
    setIsRefreshing(true);
    try {
      const headers = getAuthHeaders();
      // Trigger compliance audit
      const auditRes = await fetch(`${API_URL}/api/ai/compliance-audit`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ pursuit_id: pursuitId })
      });
      
      if (auditRes.ok) {
        const auditData = await auditRes.json();
        // Store compliance insight for display and snapshot
        setComplianceInsight(auditData);
        
        // Refresh pursuit data to get updated pwin
        const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, { headers });
        if (pursuitRes.ok) {
          setPursuit(await pursuitRes.json());
        }

        // v3.11.15 — Re-fetch the live composite + notify other listeners
        // (Reasoning Trace, PwinFeedbackLoop) so all panels stay in sync.
        try {
          const compRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/pwin-composite`, { headers });
          if (compRes.ok) setPwinComposite(await compRes.json());
        } catch (e) { /* non-blocking */ }
        window.dispatchEvent(new CustomEvent('pwin-signal-updated', {
          detail: { pursuitId, source: 'compliance-audit' }
        }));
        
        setLastRefreshed(new Date().toLocaleTimeString());
        toast.success('Compliance Audit Complete');
      } else {
        toast.error('Failed to run compliance audit');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Error running compliance audit');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Navigate to source
  const handleNavigateToSource = (source) => {
    if (source === 'Knowledge Base') {
      navigate(`/${workspaceId}/pursuit/${pursuitId}/knowledge-base`);
    } else if (source === 'Teaming Partners' || source === 'Gap Analysis') {
      navigate(`/${workspaceId}/pursuit/${pursuitId}/gaps`);
    }
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    if (!evaluation) return null;
    const scores = [
      evaluation.technical_capability,
      evaluation.past_performance_fit,
      evaluation.financial_risk,
      evaluation.management_approach,
      evaluation.competitive_positioning
    ].filter(s => s !== null && s !== undefined);
    
    if (scores.length === 0) return null;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  const overallScore = calculateOverallScore();
  // v3.11.15 — pWin source priority:
  //   1. Live composite_pwin (multi-signal blend; same value the Signal Tracker shows)
  //   2. Legacy pursuit.pwin_score / competitive_alignment_score (single AI signal)
  //   3. Local 5-Dim slider average × 20 (preliminary, before any AI signal)
  // This eliminates the previous discrepancy where Executive Verdict showed
  // the AI signal alone (e.g. 35%) while pWin Signal Tracker showed the
  // weighted composite (e.g. 30%).
  const effectiveScoreData = getEffectiveScore(pursuit);
  const scoreSource = getScoreSource(pursuit);
  const compositeValue = pwinComposite?.composite_pwin;
  const pwinPercent = (compositeValue !== null && compositeValue !== undefined)
    ? compositeValue
    : (effectiveScoreData.score ?? (overallScore ? Math.round(overallScore * 20) : null));

  // Loading state
  if (isLoading) {
    return <GoNoGoSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Unable to Load</h2>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!pursuit) return null;

  return (
    <div data-testid="go-no-go-dashboard-live" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.20 centered to max-w-7xl */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {pursuit.agency_code && (
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs">{pursuit.agency_code}</span>
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {pursuit.opportunity_name}
              </h1>
              <p className="text-sm text-slate-500">
                {pursuit.solicitation_number && `RFP #${pursuit.solicitation_number} • `}
                {pursuit.days_remaining !== null ? `Due in ${pursuit.days_remaining} days` : 'No deadline set'}
              </p>
              {/* Collaboration context — same arrangement summary that the
                  Intake screen surfaces, so reviewers + bid managers see who
                  owns vs who was invited to this pursuit at a glance. */}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {pursuit.origin === 'INVITED' && (
                  <OriginBadge pursuit={pursuit} viewerRole={user?.role} />
                )}
                <CollaborationFootnote
                  pursuit={pursuit}
                  viewerRole={user?.role}
                  viewerOrgId={user?.organizationId}
                />
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <SavingIndicator isSaving={isSaving} lastSaved={lastSaved} error={saveError} />
            {/* Invite Reviewer Button - Only for Contractor Admin or Super Admin */}
            {(user?.role === 'CONTRACTOR_ADMIN' || user?.role === 'SUPER_ADMIN') && (
              <Button 
                variant="outline" 
                className="border-purple-200 text-purple-600 hover:bg-purple-50"
                onClick={() => setShowInviteModal(true)}
                data-testid="invite-reviewer-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Invite Reviewer</span>
              </Button>
            )}
            <Button 
              variant="outline" 
              className="border-blue-200 text-blue-600 hover:bg-blue-50"
              onClick={handleRefreshScore}
              disabled={isRefreshing}
              data-testid="refresh-score-btn"
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", isRefreshing && "animate-spin")} />
              {isRefreshing ? 'Recalculating…' : 'Refresh Score'}
            </Button>
            {/* Background audit indicator — only shown while an audit is running */}
            {auditStatus === 'RUNNING' && (
              <Badge className="bg-blue-50 text-blue-600 border border-blue-200" title="Compliance audit running in the background" data-testid="audit-running-badge">
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" style={{ animationDuration: '1.5s' }} />
                Audit running…
              </Badge>
            )}
          </div>
        </div>
        
        {/* P0: Tabs for Analysis vs History */}
        <div className="flex items-center justify-between mt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="analysis" className="data-[state=active]:bg-white">
                <Target className="w-4 h-4 mr-2" />
                Analysis
              </TabsTrigger>
              <TabsTrigger value="history" className="data-[state=active]:bg-white" data-testid="decision-history-tab">
                <History className="w-4 h-4 mr-2" />
                Decision History
              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-4 text-xs text-slate-500">
            {lastRefreshed && (
              <span className="text-emerald-600">Score refreshed at {lastRefreshed}</span>
            )}
          </div>
        </div>
        </div>
      </div>

      <div className="px-8">
        <div className="max-w-7xl mx-auto">
          <PursuitLifecycleStepper />
        </div>
      </div>

      {/* P0: Invite Reviewer Modal */}
      <InviteReviewerModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        pursuitId={pursuitId}
        pursuitName={pursuit?.opportunity_name}
        onInviteSuccess={() => toast.success('Reviewer invited successfully')}
      />

      {/* Content based on active tab */}
      {activeTab === 'history' ? (
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
            <DecisionHistory 
              key={decisionHistoryKey} 
              pursuitId={pursuitId} 
            />
          </div>
        </div>
      ) : (
        <div className="p-8">
          <div className="max-w-7xl mx-auto">
        {/* v3.18.1 — Federal Template Tools Status was moved INTO the
            Executive Verdict's column 1 (under the pWin ring + AI Signal)
            so the diagnostic for *why* pWin is what it is sits next to
            the pWin reading itself. The standalone top section is gone. */}

        {/* ========================================== */}
        {/* SECTION 1: Executive Verdict */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Executive Verdict
          </h2>
        </div>
        <Card className="border-slate-200 bg-white mb-8 shadow-sm">
          <CardContent className="p-6">
            {/* Score Source Badge */}
            <div className="flex justify-end mb-4">
              <ScoreSourceBadge source={scoreSource} compact={false} />
            </div>

            {/* Analysis Required Notice */}
            {scoreSource === SCORE_SOURCE.ANALYSIS_REQUIRED && (
              <PreliminaryScoreNotice className="mb-6" />
            )}

            {/* Prerequisites Incomplete Notice */}
            {scoreSource === SCORE_SOURCE.PREREQUISITES_INCOMPLETE && (
              <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg mb-6">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Prerequisites Incomplete</p>
                  <p className="text-xs text-red-700 mt-1">
                    Complete required metadata fields and upload documents before the Fit Score can be calculated.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* pWin Ring & Verdict */}
              <div className="flex flex-col items-center">
                <PwinRingChart 
                  pwin={pwinPercent}
                  riskCounts={riskCounts}
                />
                
                <div className="flex items-center gap-4 mt-4" data-testid="risk-legend">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                    <span className="text-xs text-slate-600">
                      Low <span className="font-semibold text-slate-800">({riskCounts.low})</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <span className="text-xs text-slate-600">
                      Medium <span className="font-semibold text-slate-800">({riskCounts.medium})</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="text-xs text-slate-600">
                      High <span className="font-semibold text-slate-800">({riskCounts.high})</span>
                    </span>
                  </div>
                </div>
                {riskCounts.total === 0 && (
                  <p className="text-[11px] text-slate-400 italic mt-1.5">
                    Add risks in the heatmap below to see distribution
                  </p>
                )}
                
                {/* v3.17.2 — Demoted AI signal: neutral colors, guidance-only wording.
                    The user's Bid Decision (right column) is now the visually loud verdict,
                    not the AI suggestion. */}
                <div
                  className="mt-4 px-4 py-2.5 rounded-md w-full bg-slate-50 border border-slate-200"
                  data-testid="ai-recommendation-box"
                >
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    AI Signal{scoreSource !== SCORE_SOURCE.AI_ANALYZED ? ' · Pending' : ''}
                  </p>
                  <p
                    className="text-sm font-semibold text-slate-700 mt-0.5"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {scoreSource !== SCORE_SOURCE.AI_ANALYZED ? 'Awaiting analysis' :
                     pwinPercent >= 75 ? 'AI suggests: Favorable' :
                     pwinPercent >= 60 ? 'AI suggests: Caution' :
                     pwinPercent ? 'AI suggests: Unfavorable' : 'Incomplete'}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5 italic">
                    Guidance only — not recorded. Your team's decision is on the right.
                  </p>
                </div>

                {/* v3.18.1 — Federal Template Tools checklist relocated here.
                    Causal story: pWin → AI verdict → "why this score is low".
                    Each row is clickable and navigates to the destination. */}
                <FederalTemplateChecklist
                  pursuitId={pursuitId}
                  authToken={authToken}
                />
              </div>
              
              {/* v3.17.2 — Promoted Your Bid Decision: blue accent border, "Decision Required"
                  pill when empty, big colored Team Decision badge when set. This column
                  now visually outshouts the AI Signal column. */}
              <div>
                <div
                  className={cn(
                    "mb-4 rounded-xl border-2 p-4 transition-colors",
                    !evaluation?.decision && "border-blue-300 bg-blue-50/40",
                    evaluation?.decision === 'GO' && "border-emerald-300 bg-emerald-50/40",
                    evaluation?.decision === 'PURSUING' && "border-amber-300 bg-amber-50/40",
                    evaluation?.decision === 'NO_GO' && "border-red-300 bg-red-50/40",
                  )}
                  data-testid="your-bid-decision-panel"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <Settings2 className="w-4 h-4 text-blue-600" />
                      <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Your Bid Decision
                      </h3>
                    </div>
                    {!evaluation?.decision && (
                      <Badge
                        variant="outline"
                        className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] uppercase tracking-wider px-2 py-0.5"
                        data-testid="decision-required-pill"
                      >
                        Decision Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    Record the team's decision and rationale. Saved to the pursuit audit trail.
                  </p>

                  {/* Big colored Team Decision badge once a decision is set */}
                  {evaluation?.decision && (
                    <div
                      className={cn(
                        "mb-3 px-4 py-2.5 rounded-lg border text-center",
                        evaluation.decision === 'GO' && "bg-emerald-500 border-emerald-600 text-white",
                        evaluation.decision === 'PURSUING' && "bg-amber-500 border-amber-600 text-white",
                        evaluation.decision === 'NO_GO' && "bg-red-500 border-red-600 text-white"
                      )}
                      data-testid="team-decision-badge"
                    >
                      <p className="text-[10px] uppercase tracking-wider opacity-90">Team Decision</p>
                      <p className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {evaluation.decision === 'GO' ? 'GO' :
                         evaluation.decision === 'PURSUING' ? 'GO (Conditional)' :
                         'NO-GO'}
                      </p>
                    </div>
                  )}

                  <DecisionBadges
                    currentDecision={evaluation?.decision}
                    onSelect={handleDecisionChange}
                    disabled={isCreatingSnapshot}
                  />

                  {/* Decision Notes - Required for NO_GO */}
                  <div className="mt-3">
                    <Textarea
                      placeholder={evaluation?.decision === 'NO_GO' 
                        ? "Decision notes required for No-Go..." 
                        : "Add decision notes (optional)..."}
                      value={decisionNotes}
                      onChange={(e) => setDecisionNotes(e.target.value)}
                      className={cn(
                        "text-sm resize-none h-20",
                        evaluation?.decision === 'NO_GO' && !decisionNotes.trim() 
                          ? "border-red-300 focus:border-red-500" 
                          : ""
                      )}
                      data-testid="decision-notes-textarea"
                    />
                    {evaluation?.decision === 'NO_GO' && !decisionNotes.trim() && (
                      <p className="text-xs text-red-500 mt-1">Notes required for No-Go decisions</p>
                    )}
                    {isCreatingSnapshot && (
                      <p className="text-xs text-blue-600 mt-1 flex items-center gap-1.5">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Recording decision…
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Quick Metrics</h4>
                  <div className="space-y-2 text-sm">
                    <button
                      type="button"
                      onClick={() => {
                        const el = document.querySelector('[data-section="five-dim-scoring"]');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }}
                      className="w-full flex justify-between items-center text-left hover:bg-slate-100 rounded px-1 -mx-1 py-0.5 transition-colors group"
                      data-testid="five-dim-avg-link"
                      title="Jump to the 5-Dimension Scoring section"
                    >
                      <span className="text-slate-600 flex items-center gap-1">
                        5-Dimension Avg
                        <Link2 className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                      <span className="font-semibold text-blue-600">
                        {overallScore ? `${overallScore}/5` : <span className="text-xs text-slate-400 italic font-normal">Not scored yet</span>}
                      </span>
                    </button>
                    {/* Resource Estimate - Complexity Rating Widget */}
                    <div className={cn("flex items-center justify-between rounded-md p-2 -mx-2", getComplexityRating(requirementCount).bgColor)}>
                      <span className={getComplexityRating(requirementCount).color}>Complexity</span>
                      <div className="text-right">
                        <span className={cn("font-bold", getComplexityRating(requirementCount).color)}>
                          {getComplexityRating(requirementCount).label}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          ({requirementCount} requirements)
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 pl-2 -mt-1">
                      <span>Estimated Effort</span>
                      <span>{getComplexityRating(requirementCount).effort}</span>
                    </div>
                    {/* Self-Score Coverage heatmap (federal 0-3 rubric).
                        v3.11.5: always renders; component shows a CTA when empty
                        instead of being hidden (federal-tools discoverability). */}
                    <div className="pt-2 mt-2 border-t border-slate-200">
                      <SelfScoreHeatmap
                        summary={selfScoreSummary}
                        onNavigate={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/intake`)}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Strengths vs Gaps */}
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Key Strengths ({unifiedGaps.filter(g => g.status === 'MET').length})
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {unifiedGaps.filter(g => g.status === 'MET').slice(0, 3).map((gap) => (
                      <div key={gap.id} className="p-2 bg-emerald-50 rounded border border-emerald-100">
                        <p className="text-sm text-emerald-800">{gap.requirement_name}</p>
                        {gap.requirement_source && (
                          <p className="text-xs text-emerald-600 mt-1">Source: {gap.requirement_source}</p>
                        )}
                      </div>
                    ))}
                    {unifiedGaps.filter(g => g.status === 'MET').length === 0 && (
                      <div
                        className="p-3 bg-slate-50 border border-dashed border-slate-200 rounded-md"
                        data-testid="strengths-empty-state"
                      >
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {extractedReqs.length > 0
                            ? <>No strengths recorded yet. Set a requirement&apos;s <strong className="text-slate-800">Compliance status</strong> to <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-emerald-50 border-emerald-200 text-emerald-700 mx-0.5">MET</Badge> in the Requirements table to surface it here.</>
                            : <>No requirements extracted yet. Add them in <strong className="text-slate-800">Gap Analysis</strong> or run the AI Shredder in Opportunity Intake to track strengths and gaps.</>
                          }
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(
                            extractedReqs.length > 0
                              ? `/${workspaceId}/pursuit/${pursuitId}/intake`
                              : `/${workspaceId}/pursuit/${pursuitId}/gap-analysis`,
                          )}
                          className="mt-2 h-7 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 px-2"
                          data-testid="strengths-empty-cta"
                        >
                          {extractedReqs.length > 0 ? 'Open Requirements table →' : 'Open Gap Analysis →'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Critical Gaps ({unifiedGaps.filter(g => g.status === 'GAP').length})
                  </h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {unifiedGaps.filter(g => g.status === 'GAP').slice(0, 3).map((gap) => (
                      <div key={gap.id} className="p-2 bg-red-50 rounded border border-red-100">
                        <p className="text-sm text-red-800">{gap.requirement_name}</p>
                        {gap.requirement_source && (
                          <p className="text-xs text-red-600 mt-1">Source: {gap.requirement_source}</p>
                        )}
                      </div>
                    ))}
                    {unifiedGaps.filter(g => g.status === 'GAP').length === 0 && (
                      <div
                        className="p-3 bg-slate-50 border border-dashed border-slate-200 rounded-md"
                        data-testid="gaps-empty-state"
                      >
                        <p className="text-xs text-slate-600 leading-relaxed">
                          {extractedReqs.length > 0
                            ? <>No critical gaps recorded yet. Set a requirement&apos;s <strong className="text-slate-800">Compliance status</strong> to <Badge variant="outline" className="text-[10px] py-0 px-1.5 bg-red-50 border-red-200 text-red-700 mx-0.5">GAP</Badge> in the Requirements table to surface it here.</>
                            : <>No requirements extracted yet. Add them in <strong className="text-slate-800">Gap Analysis</strong> to track gaps.</>
                          }
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(
                            extractedReqs.length > 0
                              ? `/${workspaceId}/pursuit/${pursuitId}/intake`
                              : `/${workspaceId}/pursuit/${pursuitId}/gap-analysis`,
                          )}
                          className="mt-2 h-7 text-xs text-red-700 hover:text-red-800 hover:bg-red-50 px-2"
                          data-testid="gaps-empty-cta"
                        >
                          {extractedReqs.length > 0 ? 'Open Requirements table →' : 'Open Gap Analysis →'}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {unifiedGaps.filter(g => g.status === 'GAP').length > 0 && (
                    <Button 
                      variant="outline" 
                      className="w-full mt-3 border-red-200 text-red-700 hover:bg-red-50"
                      onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gaps`)}
                      data-testid="find-teaming-partner-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Find Teaming Partner
                    </Button>
                  )}
                </div>
                
                {/* Compliance Insight Card - AI Reasoning */}
                {complianceInsight && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100" data-testid="compliance-insight">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <h4 className="font-semibold text-blue-900 text-sm">Compliance Insight</h4>
                    </div>
                    <p className="text-sm text-blue-800">
                      {complianceInsight.status_recommendation?.reason || 'Run Compliance Audit to generate AI insights'}
                    </p>
                    {complianceInsight.red_flags?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {complianceInsight.red_flags.slice(0, 2).map((flag, idx) => (
                          <Badge key={flag.flag || `red-flag-${idx}`} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                            {flag.flag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========================================== */}
        {/* SECTION 2: 5-Dimension Bid Scoring */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4" data-section="five-dim-scoring">
          <Target className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            5-Dimension Bid Scoring
          </h2>
          <span className="text-xs text-slate-400 ml-auto italic">Equally weighted average · feeds 5-Dim Avg in Quick Metrics</span>
        </div>
        <Card className="border-slate-200 bg-white shadow-sm mb-8">
          <CardContent className="pt-6">
            {/* pWin-lock transparency banner — clarifies what sliders actually affect */}
            {scoreSource === SCORE_SOURCE.AI_ANALYZED ? (
              <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start gap-2" data-testid="pwin-lock-banner">
                <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-800">
                    Sliders feed the 5-Dimension Average
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    The pWin ring above shows the live <span className="font-medium">composite pWin</span> (the same value as the pWin Signal Tracker). Your slider adjustments update the <span className="font-medium">5-Dim Avg</span> metric in the Quick Metrics — they do not directly move the composite ring.
                  </p>
                </div>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="pwin-preliminary-banner">
                <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Preliminary scoring — AI analysis has not run yet
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Until documents are uploaded and AI analysis completes, these sliders are the primary input to the pWin score. Once AI analysis runs, pWin will lock to the AI result and sliders will only feed the 5-Dim Avg.
                  </p>
                </div>
              </div>
            )}

            {/* Auto-seeded notification — shown until user modifies any dimension */}
            {evaluation?.auto_seeded && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2" data-testid="auto-seeded-banner">
                <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Scores Auto-Generated from Opportunity Intake
                  </p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    These initial scores were calculated based on your pursuit data. Adjust the sliders below to reflect your actual competitive position.
                  </p>
                </div>
              </div>
            )}

            {/* Reset to AI defaults — visible only when the user has modified the auto-seeded values */}
            {!evaluation?.auto_seeded && hasAnyDimensionScore && (
              <div className="mb-4 flex items-center justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetToAiDefaults}
                  disabled={isResettingToAi}
                  className="text-xs text-slate-500 hover:text-blue-600 hover:bg-blue-50 h-8"
                  data-testid="reset-to-ai-defaults-btn"
                  title="Overwrite your slider values with the AI-derived defaults from Opportunity Intake"
                >
                  {isResettingToAi ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                      Resetting…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3 h-3 mr-1.5" />
                      Reset to AI defaults
                    </>
                  )}
                </Button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <DimensionScoreRow
                label="Technical Capability"
                description="How well does your organization meet requirements?"
                value={evaluation?.technical_capability}
                onChange={(v) => handleScoreChange('technical_capability', v)}
                icon={Settings2}
                accent="blue"
                hint={
                  selfScoreSummary && selfScoreSummary.scored_count > 0 ? (
                    <button
                      type="button"
                      data-testid="dim-tech-self-score-hint"
                      onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/intake`)}
                      className="mt-2 w-full text-left text-[11px] text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded px-2 py-1.5 transition-colors"
                      title="View Requirements table on Opportunity Intake"
                    >
                      <span className="font-semibold">SME walk:</span>{' '}
                      {selfScoreSummary.scored_pct}% of {selfScoreSummary.total_requirements} reqs scored
                      {selfScoreSummary.average_score !== null && (
                        <> · avg {selfScoreSummary.average_score}/3</>
                      )}
                      {' · '}
                      <span className="underline">View →</span>
                    </button>
                  ) : null
                }
              />
              <DimensionScoreRow
                label="Past Performance Fit"
                description="How relevant is your past performance?"
                value={evaluation?.past_performance_fit}
                onChange={(v) => handleScoreChange('past_performance_fit', v)}
                icon={Award}
                accent="emerald"
                hint={
                  selfScoreSummary && selfScoreSummary.scored_count > 0 ? (
                    <button
                      type="button"
                      data-testid="dim-pp-self-score-hint"
                      onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/intake`)}
                      className="mt-2 w-full text-left text-[11px] text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded px-2 py-1.5 transition-colors"
                      title="View Requirements table on Opportunity Intake"
                    >
                      <span className="font-semibold">PP-backed reqs:</span>{' '}
                      {selfScoreSummary.strong_fit_count}/{selfScoreSummary.scored_count} scored ≥2 (Green/Blue)
                      {' · '}
                      <span className="underline">View →</span>
                    </button>
                  ) : null
                }
              />
              <DimensionScoreRow
                label="Financial Feasibility"
                description="Financial feasibility (5 = low risk)"
                value={evaluation?.financial_risk}
                onChange={(v) => handleScoreChange('financial_risk', v)}
                icon={BarChart3}
                accent="amber"
              />
              <DimensionScoreRow
                label="Management Approach"
                description="Strength of management & organization"
                value={evaluation?.management_approach}
                onChange={(v) => handleScoreChange('management_approach', v)}
                icon={Users}
                accent="purple"
              />
              <DimensionScoreRow
                label="Competitive Positioning"
                description="Advantage vs known competitors"
                value={evaluation?.competitive_positioning}
                onChange={(v) => handleScoreChange('competitive_positioning', v)}
                icon={Globe}
                accent="rose"
              />
            </div>

            {/* Pursuit Metadata — categorical, doesn't feed the 5-Dim average */}
            <div className="mt-6 pt-5 border-t border-slate-100">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-3 font-semibold">
                Pursuit Metadata
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-white rounded-lg border border-slate-200" data-testid="teaming-status-card">
                  <div className="flex items-center gap-2 mb-1">
                    <UserPlus className="w-4 h-4 text-slate-500" />
                    <span className="font-medium text-slate-800 text-sm">Teaming Status</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-3">Where the partner pipeline stands for this pursuit.</p>
                  <select
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                    value={evaluation?.teaming_status ?? ''}
                    onChange={(e) => handleScoreChange('teaming_status', e.target.value || null)}
                    data-testid="teaming-status-select"
                  >
                    <option value="">— Select —</option>
                    <option value="NOT_STARTED">Not Started</option>
                    <option value="EVALUATING">Evaluating Partners</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="COMPLETE">Complete</option>
                  </select>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ========================================== */}
        {/* SECTION 2.5: Bid Insights (v3.17.2 redesign) */}
        {/* Unified section wrapping: Self-Score Roll-up by Task Area,    */}
        {/* Cross-Tool Coverage Map (promoted from a tiny button into a   */}
        {/* teaser card), and Prior Proposals — Similar Evaluation        */}
        {/* Criteria. Restores standard mb-4 / mb-8 rhythm before the     */}
        {/* Bid Pursuit Scorecard heading and gives the three widgets a   */}
        {/* single visual home.                                           */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-cyan-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bid Insights
          </h2>
          <span className="text-xs text-slate-400 ml-auto italic">
            Roll-ups · Cross-tool coverage · Prior wins with similar evaluation criteria
          </span>
        </div>
        <div className="space-y-6 mb-8" data-testid="bid-insights-section">
          <SelfScoreRollupCard
            pursuitId={pursuitId}
            authToken={authToken}
            apiUrl={API_URL}
            onNavigate={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/intake`)}
          />

          <CrossToolCoverageMapTeaser
            pursuitId={pursuitId}
            authToken={authToken}
          />

          <PriorProposalsPanel pursuitId={pursuitId} />
        </div>

        {/* ========================================== */}
        {/* SECTION 3: Bid Pursuit Scorecard */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Bid Pursuit Scorecard
          </h2>
        </div>
        <div className="mb-8" data-testid="scorecard-section">
          <BidPursuitScorecard pursuitId={pursuitId} token={authToken} />
        </div>

        {/* ========================================== */}
        {/* SECTION 4: pWin Signal Tracker */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-purple-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            pWin Signal Tracker
          </h2>
          <span className="text-xs text-slate-400 ml-auto italic">Weighted blend of AI Analysis, Scorecard, Capability Matrix, and Compliance Matrix</span>
        </div>
        <div className="mb-8" data-testid="pwin-loop-section">
          <PwinFeedbackLoop pursuitId={pursuitId} token={authToken} workspaceId={workspaceId} />
        </div>

        {/* ========================================== */}
        {/* SECTION 5: Coverage, Competitors & What-If */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Coverage, Competitors & What-If
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="space-y-6">
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader className="pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Requirement Coverage
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <RequirementCoverage gaps={unifiedGaps} />

                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Button
                    variant="outline"
                    className="w-full border-blue-200 text-blue-600"
                    onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gaps`)}
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    View Gap Analysis
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* v3.17.2 — Coverage by Source pie chart relocated from above the
                Bid Pursuit Scorecard into the Requirement Coverage column so
                all "which requirements are covered / by whom" insights sit
                together in one vertical stack. */}
            <CoverageBySourcePanel pursuitId={pursuitId} />
          </div>
          
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-slate-600" />
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Competitive Analysis
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-4 max-h-[500px] overflow-y-auto">
              <CompetitiveIntelligence 
                competitors={competitors}
                onAddCompetitor={handleAddCompetitor}
                onDeleteCompetitor={handleDeleteCompetitor}
                onUpdateCompetitor={handleUpdateCompetitor}
              />
              
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button 
                  variant="outline" 
                  className="w-full border-slate-200 text-slate-700"
                  onClick={() => {
                    const q = encodeURIComponent(
                      pursuit?.agency_name ||
                      pursuit?.solicitation_number ||
                      pursuit?.opportunity_name ||
                      ''
                    );
                    window.open(
                      q
                        ? `https://sam.gov/search/?index=opp&q=${q}`
                        : 'https://sam.gov/search/?index=opp',
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                  data-testid="research-sam-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Research on SAM.gov
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-lg font-semibold text-slate-900">
                  What-If Scenarios
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <WhatIfSimulator 
                scenarios={whatIfScenarios}
                onToggle={handleWhatIfToggle}
                basePwin={pwinPercent}
                onAddScenario={handleAddWhatIfScenario}
                onDeleteScenario={handleDeleteWhatIfScenario}
              />
              
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button 
                  variant="outline" 
                  className="w-full border-amber-200 text-amber-700 hover:text-amber-800"
                  onClick={() => navigate(`/${workspaceId}/settings/pwin-config`)}
                  data-testid="configure-pwin-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Configure pWin Weights
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ========================================== */}
        {/* SECTION 6: Risk Heatmap & Mitigation */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-red-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Risk Heatmap & Mitigation
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-200 bg-white shadow-sm lg:col-span-2">
            <CardHeader className="pb-4 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-600" />
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Risk Heatmap (5x5)
                  </CardTitle>
                </div>
                <span className="text-xs text-slate-500">Likelihood vs. Impact</span>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <RiskHeatmap
                risks={risks}
                onCellFocusRisk={handleFocusRiskInMitigations}
                onAddRiskAt={(pos) => {
                  // Quick-add an empty named risk at the clicked severity cell.
                  // User fills in the name via the Add Risk flow; prefilling
                  // likelihood/impact prevents an obvious source of user error.
                  const defaultName = `New risk (${pos.likelihood.toLowerCase()} × ${pos.impact.toLowerCase().replace('_', ' ')})`;
                  handleAddRisk({
                    name: defaultName,
                    likelihood: pos.likelihood,
                    impact: pos.impact,
                    severity: 'MEDIUM',
                  });
                }}
              />

              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  className="w-full border-slate-200 text-slate-700"
                  onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/compliance`)}
                  data-testid="open-compliance-tracker-btn"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Open Compliance Tracker
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 bg-white shadow-sm">
            <CardHeader className="pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-600" />
                <CardTitle className="text-lg font-semibold text-slate-900">
                  Risk Mitigation
                </CardTitle>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Strategies for high-risk items.
              </p>
            </CardHeader>
            <CardContent className="pt-4 max-h-[400px] overflow-y-auto">
              <RiskMitigationPanel 
                mitigations={mitigations}
                highlightedMitigationId={highlightedMitigationId}
                onNavigateToSource={handleNavigateToSource}
                onAddMitigation={handleAddMitigation}
                onDeleteMitigation={handleDeleteMitigation}
                onUpdateMitigation={handleUpdateMitigation}
              />
            </CardContent>
          </Card>
        </div>
        
        {/* ========================================== */}
        {/* SECTION 7: AI Reasoning Trace */}
        {/* ========================================== */}
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            AI Reasoning Trace
          </h2>
        </div>
        <ReasoningTraceViewer 
          pursuitId={pursuitId}
          authToken={authToken}
          className="mb-8"
        />

        {/* ========================================== */}
        {/* FOOTER ACTIONS */}
        {/* ========================================== */}
        {/* ========================================== */}
        {/* FOOTER ACTIONS */}
        {/* ========================================== */}
        <div
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 bg-white rounded-lg border border-slate-200 shadow-sm"
          data-testid="gonogo-footer-actions"
        >
          <div className="text-xs text-slate-500 min-w-0">
            <p className="font-medium mb-1">Fit Score is computed from:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>5-Dimension Bid Scoring</li>
              <li>35-question Bid Pursuit Scorecard</li>
              <li>pWin composite signals</li>
              <li>Requirements coverage &amp; Capability Matrix alignment</li>
            </ul>
            {hasUnsavedRationale && (
              <p
                className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5"
                data-testid="unsaved-rationale-indicator"
                title="Your decision rationale hasn't been recorded in a snapshot yet — it will be included if you click Hard No-Go or trigger another decision."
              >
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
                Unsaved decision rationale
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 md:gap-3">
            {/* Destructive action — outlined, muted red. Second-rightmost. */}
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setShowNoGoConfirm(true)}
              disabled={isCreatingSnapshot}
              data-testid="mark-no-go-btn"
            >
              {isCreatingSnapshot ? (
                <>
                  <span className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                'Mark as Hard No-Go'
              )}
            </Button>

            {/* Primary CTA — v3.18.10 lifecycle: GoNoGo → Gaps (next step). */}
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gaps`)}
              disabled={isCreatingSnapshot}
              data-testid="proceed-to-gaps-btn"
            >
              Proceed to Gap Analysis
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Destructive-action confirm dialog for "Mark as Hard No-Go" */}
        <AlertDialog open={showNoGoConfirm} onOpenChange={setShowNoGoConfirm}>
          <AlertDialogContent data-testid="no-go-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Mark this pursuit as Hard No-Go?</AlertDialogTitle>
              <AlertDialogDescription>
                This records an immutable No-Go decision snapshot for the pursuit and
                freezes the current compliance state. The history is preserved for audit,
                but you cannot un-record this event — only create a new decision later.
                {!decisionNotes.trim() && (
                  <span className="block mt-2 text-red-600 font-medium">
                    Decision notes are required. Please add a rationale above before confirming.
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <div
                className="hidden md:flex items-center gap-1 text-[11px] text-slate-400 mr-auto"
                aria-hidden="true"
              >
                <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono text-[10px]">Esc</kbd>
                <span>to cancel</span>
              </div>
              <AlertDialogCancel data-testid="no-go-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!decisionNotes.trim() || isCreatingSnapshot}
                onClick={async () => {
                  await handleDecisionChange('NO_GO');
                }}
                data-testid="no-go-confirm-btn"
              >
                {isCreatingSnapshot ? 'Recording…' : 'Yes, mark as No-Go'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      </div>
      )}
    </div>
  );
}
